/**
 * Profile-update FAIL payload builders (Issue #181 correction).
 * Kept free of axios/action imports so unit tests can import it directly.
 *
 * Payload shapes:
 *   - string — legacy / persistence failures (reducer-compatible)
 *   - { message, fieldErrors } — DRF field/detail errors with UA copy
 * Never surfaces bare Axios "Request failed with status code 400".
 */

const localizeProfileHandleError = (raw) => {
  const message = typeof raw === "string" ? raw : String(raw ?? "");
  if (!message) return "Не вдалося оновити ім'я посилання.";
  if (/already exists/i.test(message)) {
    return "Користувач з таким ім'ям посилання вже існує.";
  }
  if (/Handle is required/i.test(message) || /^required$/i.test(message)) {
    return "Вкажіть ім'я посилання на сайті.";
  }
  if (
    /must be 3/i.test(message)
    || /lowercase/i.test(message)
    || /hyphen/i.test(message)
    || /invalid/i.test(message)
  ) {
    return (
      "Ім'я посилання має містити 3–150 символів: починатися з малої "
      + "латинської літери, закінчуватися літерою або цифрою, і містити "
      + "лише a–z, 0–9, _ або -."
    );
  }
  return message;
};

const PROFILE_CONTENT_FIELDS = ["first_name", "last_name", "description"];

const pickFirstErrorString = (value) => {
  if (typeof value === "string") return value;
  if (Array.isArray(value) && value.length > 0) {
    return pickFirstErrorString(value[0]);
  }
  if (value && typeof value === "object") {
    if (typeof value.string === "string") return value.string;
    if (typeof value.message === "string") return value.message;
    if (typeof value.detail === "string") return value.detail;
  }
  return undefined;
};

export const buildUserProfileUpdateFailPayload = (error) => {
  if (!error) {
    return {
      message: "Не вдалося оновити профіль.",
      fieldErrors: {},
    };
  }

  // Persistence / non-HTTP failures stay plain strings for existing tests.
  if (!error.response) {
    return error.message || "Не вдалося оновити профіль.";
  }

  const data = error.response.data;
  if (data && typeof data === "object" && !Array.isArray(data)) {
    const fieldErrors = {};
    const handleRaw = pickFirstErrorString(data.handle);
    if (handleRaw) {
      fieldErrors.handle = localizeProfileHandleError(handleRaw);
    }
    PROFILE_CONTENT_FIELDS.forEach((field) => {
      const raw = pickFirstErrorString(data[field]);
      if (raw) {
        fieldErrors[field] = raw;
      }
    });

    const detail = pickFirstErrorString(data.detail)
      || pickFirstErrorString(data.non_field_errors);

    if (Object.keys(fieldErrors).length > 0) {
      return {
        message:
          fieldErrors.handle
          || fieldErrors.first_name
          || fieldErrors.last_name
          || fieldErrors.description
          || detail
          || "Не вдалося оновити профіль.",
        fieldErrors,
      };
    }

    if (detail) {
      return { message: detail, fieldErrors: {} };
    }
  }

  if (typeof data === "string" && data.trim()) {
    return { message: data, fieldErrors: {} };
  }

  const fallback = error.message;
  if (
    typeof fallback === "string"
    && fallback
    && !/^Request failed with status code\s*\d+/i.test(fallback)
  ) {
    return { message: fallback, fieldErrors: {} };
  }

  return {
    message: "Не вдалося оновити профіль. Перевірте заповнення форми.",
    fieldErrors: {},
  };
};
